import { ReactNode, useCallback, useMemo } from 'react';
import { PaginationState } from '@tanstack/react-table';
import { Trans, useTranslation } from '@sib/i18n';
import { Button, Select } from '@sib/ui';
import { ChevronLeft, ChevronRight } from '@sib/ui/icons';
import { DEFAULT_MIN_PAGE, pageSizeOptions } from '../../constants';

export interface DataTablePaginationProps {
  state: PaginationState;
  onChange: (value: PaginationState) => void;
  rowCount: number;
  minPage?: number;
  itemsPerPageLabel?: ReactNode;
  partOfTotalLabel?: (
    paginationState: PaginationState & {
      rowCount: number;
      currentMaxRow: number;
    }
  ) => ReactNode;
}

export const DataTablePagination = ({
  state: paginationState,
  onChange,
  rowCount,
  minPage = DEFAULT_MIN_PAGE,
  itemsPerPageLabel,
  partOfTotalLabel,
}: DataTablePaginationProps) => {
  const { t } = useTranslation();
  const currentMaxRow = useMemo(
    () =>
      minPage
        ? Math.min(
            paginationState.pageIndex * paginationState.pageSize,
            rowCount
          )
        : Math.min(
            (paginationState.pageIndex + 1) * paginationState.pageSize,
            rowCount
          ),
    [paginationState, rowCount, minPage]
  );

  const hasNextPage = currentMaxRow < rowCount;

  const goNext = useCallback(() => {
    if (!hasNextPage) return;
    onChange({ ...paginationState, pageIndex: paginationState.pageIndex + 1 });
  }, [paginationState, onChange, hasNextPage]);

  const goPrev = useCallback(() => {
    if (paginationState.pageIndex === minPage) return;
    onChange({ ...paginationState, pageIndex: paginationState.pageIndex - 1 });
  }, [paginationState, onChange, minPage]);

  const onPageSizeChange = useCallback(
    (size: string) => {
      onChange({ pageIndex: minPage, pageSize: Number(size) });
    },
    [onChange, minPage]
  );

  return (
    <div className="z-20 flex w-full shrink-0 justify-between border-t bg-white px-3 py-2">
      <div className="align-center flex justify-center gap-x-2">
        <p className="text-primary flex items-center text-sm whitespace-nowrap">
          {itemsPerPageLabel || t('components.tablePagination.itemsPerPage')}
        </p>
        <div className="w-[92px]">
          <Select
            onValueChange={onPageSizeChange}
            value={String(paginationState.pageSize)}
            options={pageSizeOptions}
          />
        </div>
      </div>
      <div className="flex items-center justify-center gap-x-2">
        <p className="text-primary flex items-center text-sm whitespace-nowrap">
          {partOfTotalLabel?.({
            ...paginationState,
            rowCount,
            currentMaxRow,
          }) || (
            <Trans
              i18nKey="components.tablePagination.partOfTotal"
              t={t}
              values={{
                part: `${(paginationState.pageIndex - minPage) * paginationState.pageSize + 1}-${currentMaxRow}`,
                total: rowCount,
              }}
              components={{
                highlight: <span className="text-muted-foreground px-0.5" />,
              }}
            />
          )}
        </p>
        <div className="flex gap-x-1">
          <Button variant="ghost" className="h-7 px-1 py-0.5" onClick={goPrev}>
            <ChevronLeft />
          </Button>
          <Button variant="ghost" className="h-7 px-1 py-0.5" onClick={goNext}>
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
};
